<template>
  <div data-test-id="form-simplified-enrollment-profile-information">
    <vf-notification
      v-if="notification.message"
      class="mb-4"
      :type="notification.type"
      @close="notification.message = ''"
    >
      {{ notification.message }}
    </vf-notification>
    <vf-progress-bar max="3" value="2" />
    <h2 class="mt-4" :class="brandClasses.subheading">
      {{ $t.basicInformation }}
    </h2>
    <base-form class="mt-4 space-y-4" :form @submit="submit">
      <vf-form-field v-slot="{ attrs }" name="firstName" :rule="form.firstName ? rules.firstName : []">
        <vf-input v-model="form.firstName" v-bind="attrs" :label="$t.firstName" name="firstName" />
      </vf-form-field>
      <vf-form-field v-slot="{ attrs }" name="lastName" :rule="form.lastName ? rules.lastName : []">
        <vf-input v-model="form.lastName" v-bind="attrs" :label="$t.lastName" name="lastName" />
      </vf-form-field>
      <vf-form-field v-slot="{ attrs }" name="postalCode" :rule="form.postalCode ? rules.postalCode : []">
        <input-postal-code
          v-model="form.postalCode"
          v-bind="{
            ...attrs,
            ...($feature.enableMaskedPostalCode && postalCodeFormat.mask ? postalCodeFormat : {}),
          }"
        />
      </vf-form-field>
      <vf-form-field
        v-slot="{ attrs }"
        :hint="$t.dateFormatHint"
        name="birthDate"
        :rule="form.birthDate ? rules.birthDate : []"
      >
        <input-date v-model="form.birthDate" v-bind="attrs" :disabled="!!user.details?.birthDate" :required="isBirthDateRequired">
          <template #after>
            <vf-toggletip placement="top-start">
              <template #trigger>
                <span class="sr-only">{{ $t.birthDate }}</span>
                <vf-icon name="info" size="md" />
              </template>
              {{ $t.birthDateTooltip }}
            </vf-toggletip>
          </template>
        </input-date>
      </vf-form-field>
      <div class="mt-6 space-y-2">
        <vf-button class="w-full" :loading type="submit">
          {{ $t.next }}
        </vf-button>
        <vf-button class="w-full" :disabled="loading" variant="secondary" @click="emit('continue', false)">
          {{ $t.skipForNow }}
        </vf-button>
      </div>
    </base-form>
  </div>
</template>

<script lang="ts" setup>
import type { BaseNotification } from '#types/notification'

const emit = defineEmits<{
  continue: [submitted: boolean]
}>()

const { getValidator, load: loadRules, formatters } = useAddressApiRules()
const { brandClasses } = useAppConfig().components.dialog.simplifiedEnrollment
const { minimalAge: minimalAgeConfig, isBirthDateRequired } = useAppConfig().components.form.signUp
const minimalAge: number = minimalAgeConfig[useCountryCode()] || minimalAgeConfig
const countryCode = useCountryCode()
const { validateDate, validateMaxDate, validateMinDate } = useLocalizedValidators()
const { $t, $feature } = useNuxtApp()
const user = useUserStore()
const auth = useAuthStore()
const timelyDismissal = useTimelyDismissal()

const today = await getServerDate()
const maxBirthDate = new Date(today.getTime())
maxBirthDate.setFullYear(today.getUTCFullYear() - minimalAge)

const loading = ref(false)

const form = reactive({
  firstName: user.details?.firstName || '',
  lastName: user.details?.lastName || '',
  postalCode: user.details?.postalCode || '',
  birthDate: user.details?.birthDate || ''
})

const postalCodeFormat = computed(() => formatters.value.postalCode || {})

const notification = reactive<BaseNotification>({
  message: '',
  type: 'info'
})

const rules = {
  firstName: getValidator('firstName', $t.firstName),
  lastName: getValidator('lastName', $t.lastName),
  postalCode: getValidator('postalCode', $t.zipCode, {
    pattern: $t.validation.postalCode,
    minLength: $t.validation.postalCodeLength,
  }),
  birthDate: [
    ...(isBirthDateRequired ? [validateRequired($t.birthDate)] : []),
    validateDate('YYYY-MM-DD', $t.dobValidationMsg),
    validateMinDate(new Date(DATE_MIN), $t.dobValidationMsg),
    validateMaxDate(maxBirthDate, replaceAll($t.dobAgeRestrictionMsg, { minimalAge })),
  ]
}

const submit = async () => {
  try {
    loading.value = true
    notification.message = ''

    const payload = Object.fromEntries(Object.entries(form).filter(([_, value]) => value))
    // Birth dates can't be updated and for loyalty enrollment, skip empty string birthDate
    if (user.details?.birthDate || (!isBirthDateRequired && !payload.birthDate))
      delete payload.birthDate

    await user.updateBasicInfo(payload)
    emit('continue', true)
  }
  catch (err) {
    assertApiError(err)
    notification.message = err.message
    notification.type = 'error'
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  if ($feature.tempEnablePostAuthOnboarding && auth.consumerNo)
    timelyDismissal.set(`se_profile_${auth.consumerNo}`)
  loadRules(countryCode.toLocaleLowerCase(), useLocale())
})
</script>
